import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, User2, Phone, Mail, Users, Link as LinkIcon, RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const empty = { name: "", phone: "", whatsapp: "", email: "", id_number: "", address: "", notes: "" };

function portalUrl(token) {
  return `${window.location.origin}/portal/${token}`;
}

export default function Tenants() {
  const { data: tenants = [], isLoading } = useSWR("/tenants", fetcher);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const filtered = tenants.filter((t) => {
    const s = q.toLowerCase();
    return !s || t.name.toLowerCase().includes(s) || (t.phone || "").includes(s) || (t.email || "").toLowerCase().includes(s);
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...t }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/tenants/${editing.id}`, form);
      else await api.post("/tenants", form);
      toast.success(editing ? "Penyewa diperbarui" : "Penyewa ditambahkan");
      setOpen(false);
      mutate("/tenants");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (t) => {
    if (!confirm(`Hapus penyewa ${t.name}?`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Penyewa dihapus");
      mutate("/tenants");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const copyPortal = async (t) => {
    const url = portalUrl(t.access_token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link portal disalin ke clipboard");
    } catch {
      // Fallback: show in prompt
      window.prompt("Salin link portal:", url);
    }
  };

  const regenerateToken = async (t) => {
    if (!confirm(`Buat ulang link portal untuk ${t.name}? Link lama akan tidak berlaku lagi.`)) return;
    try {
      await api.post(`/tenants/${t.id}/regenerate-token`);
      toast.success("Link portal baru dibuat");
      mutate("/tenants");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Data Master
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Penyewa</h1>
          <p className="text-sm text-muted-foreground mt-1">Data identitas, kontak, dan riwayat penyewa.</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Cari nama / no HP / email..." value={q} onChange={(e) => setQ(e.target.value)} className="w-full sm:w-64" data-testid="tenants-search" />
          <Button onClick={openNew} className="gap-2 shrink-0" data-testid="add-tenant-button">
            <Plus className="h-4 w-4" /> Tambah
          </Button>
        </div>
      </header>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <Users className="h-7 w-7" />
            </div>
            <div>
              <div className="font-display text-lg font-medium">Belum ada penyewa</div>
              <p className="text-sm text-muted-foreground mt-1">Tambahkan data penyewa untuk mulai membuat kontrak.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <Card key={t.id} data-testid={`tenant-card-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                    <User2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                      {t.phone && <div className="flex items-center gap-1.5 truncate"><Phone className="h-3 w-3" /> {t.phone}</div>}
                      {t.email && <div className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3" /> {t.email}</div>}
                      {t.id_number && <div className="truncate">KTP: {t.id_number}</div>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => copyPortal(t)} title="Salin link portal" data-testid={`copy-portal-${t.id}`}>
                      <LinkIcon className="h-4 w-4 text-primary" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => regenerateToken(t)} title="Buat ulang link portal" data-testid={`regen-portal-${t.id}`}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-tenant-${t.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(t)} data-testid={`delete-tenant-${t.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Penyewa" : "Tambah Penyewa"}</DialogTitle>
            <DialogDescription>Isi identitas dan kontak penyewa. Nomor WhatsApp dipakai untuk reminder ke depan.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nama Lengkap</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>No. HP</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tenant-phone-input" />
              </div>
              <div className="space-y-1.5">
                <Label>No. WhatsApp</Label>
                <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="6281..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>No. KTP</Label>
                <Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Alamat</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="tenant-save-button">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
